import React, { useMemo } from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  Sequence,
  staticFile,
} from "remotion";
import { COPY, CopyContext, type Lang } from "./copy";
import { LOOK } from "./look";
import { StageFrame } from "./stage";
import { Cta } from "./scenes/Cta";
import { Dark } from "./scenes/Dark";
import { Hook } from "./scenes/Hook";
import { Pricing } from "./scenes/Pricing";
import { Proof } from "./scenes/Proof";
import { Reveal } from "./scenes/Reveal";
import { SetCac } from "./scenes/SetCac";
import { Share } from "./scenes/Share";
import { Track } from "./scenes/Track";
import {
  DEFAULT_MUSIC,
  type MusicKey,
  type Track as MusicTrack,
  TRACKS,
} from "./music";
import {
  B2B_FPS,
  SCENE_ORDER,
  type SceneKey,
  type Timing,
  TimingContext,
  timingFor,
} from "./timing";
import { Sfx, SfxContext } from "./ui/Sfx";

export type B2BVideoProps = {
  lang: Lang;
  /** Soundtrack (see `music.ts`); the cuts follow its tempo. */
  music?: MusicKey;
  /** Sound effects on the visual events (off = music only, for comparison). */
  sfx?: boolean;
};

const SCENES: Record<SceneKey, React.FC> = {
  hook: Hook,
  dark: Dark,
  reveal: Reveal,
  setCac: SetCac,
  share: Share,
  track: Track,
  pricing: Pricing,
  proof: Proof,
  cta: Cta,
};

/** Music level under the SFX: −4 dB from the track alone (~-22 LUFS bed, ~-19 LUFS with the SFX). */
const MUSIC_BED = 0.63;
/**
 * The bed swells over the beat before the pricing cut (+1.5 dB) and again
 * before the CTA (+3 dB): the track itself is flat after the reveal, and
 * the payoff scenes should feel like a step up.
 */
const LIFT = { pricing: 1.19, cta: 1.41 };

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * The soundtrack, softened. Through the problem (hook + dark) it plays
 * muffled (low-passed stem); the filter opens over the last beat before the
 * reveal, so the full band arrives with "Frak transforme…". Equal-power
 * crossfade between the two stems (same start, same trim). The trim puts
 * the track's measured beat `startBeat` on frame 0, within half a frame.
 */
const Music: React.FC<{ track: MusicTrack; timing: Timing }> = ({
  track,
  timing,
}) => {
  const end = timing.duration;
  const open = timing.start.reveal;
  const trimBefore = Math.round(
    (track.beatPhase + (track.startBeat * 60) / track.bpm) * B2B_FPS,
  );
  const fade = (f: number) =>
    interpolate(f, [0, 4, end - 24, end], [0, 1, 1, 0], clamp);
  const mix = (f: number) =>
    interpolate(f, [open - timing.beat, open], [0, 1], clamp);
  const gain = MUSIC_BED * track.volume;
  const { pricing, cta } = timing.start;
  const lift = (f: number) =>
    interpolate(
      f,
      [pricing - timing.beat, pricing, cta - timing.beat, cta],
      [1, LIFT.pricing, LIFT.pricing, LIFT.cta],
      clamp,
    );
  return (
    <>
      <Sequence
        durationInFrames={open + 2}
        layout="none"
        name="music (muffled)"
      >
        <Audio
          src={staticFile(track.muffled)}
          trimBefore={trimBefore}
          volume={(f) => fade(f) * gain * Math.cos((mix(f) * Math.PI) / 2)}
        />
      </Sequence>
      <Audio
        src={staticFile(track.file)}
        trimBefore={trimBefore}
        volume={(f) =>
          fade(f) * lift(f) * gain * Math.sin((mix(f) * Math.PI) / 2)
        }
      />
    </>
  );
};

/**
 * B2B partner video (~42s; landscape master + 4:5 feed + square, see
 * `stage.tsx`). For the /partner page and LinkedIn, where it plays muted:
 * every idea is on screen, one per scene. Sound is a bonus layer: a soft
 * music bed plus one effect per visual event (`ui/Sfx.tsx`).
 *
 *   CAC +60 % → your customers already recommend you… but it lands as
 *   "direct" → Frak turns them into an acquisition channel
 *   → 1 set your CAC / 2 customers share a tracked link / 3 every sale
 *   attributed → 20 % on sales only → proof + live brands → Founding Brands
 *
 * Every scene is a hard cut on a downbeat of the chosen soundtrack (see
 * `timing.ts` / `music.ts`); the duration follows its tempo, via
 * `calculateB2BMetadata` in `Root.tsx`.
 */
export const B2BVideo: React.FC<B2BVideoProps> = ({
  lang,
  music = DEFAULT_MUSIC,
  sfx = true,
}) => {
  const timing = useMemo(() => timingFor(music), [music]);
  return (
    <CopyContext.Provider value={COPY[lang]}>
      <TimingContext.Provider value={timing}>
        <SfxContext.Provider value={sfx}>
          <AbsoluteFill style={{ background: LOOK.white }}>
            <Music track={TRACKS[music]} timing={timing} />
            {/* Cut sounds live here: a whoosh peaks on the cut, so it starts in the previous scene. */}
            {SCENE_ORDER.slice(1).map((key) => (
              <Sfx
                key={key}
                sound={key === "reveal" ? "impact" : "swoosh"}
                at={timing.start[key]}
              />
            ))}
            <StageFrame>
              {SCENE_ORDER.map((key) => {
                const Scene = SCENES[key];
                return (
                  <Sequence
                    key={key}
                    name={key}
                    from={timing.start[key]}
                    durationInFrames={timing.frames[key]}
                    premountFor={B2B_FPS}
                  >
                    <Scene />
                  </Sequence>
                );
              })}
            </StageFrame>
          </AbsoluteFill>
        </SfxContext.Provider>
      </TimingContext.Provider>
    </CopyContext.Provider>
  );
};

/** Duration follows the soundtrack's tempo. */
export const calculateB2BMetadata = ({ props }: { props: B2BVideoProps }) => ({
  durationInFrames: timingFor(props.music ?? DEFAULT_MUSIC).duration,
});
